// @ts-strict-ignore
import { component } from "picoapp";
import { createFocusTrap } from "focus-trap";
import {
  createCardPlaceholders,
  debounce,
  getFullyQualifiedUrl,
} from "../../utilities";

type Resource = "product" | "page" | "article" | "collection";
const getPredictiveSearchRequestUrl = (
  searchTerm: string,
  sectionId: string,
  resourceTypes: Resource[],
  limit: number
) =>
  getFullyQualifiedUrl(`${window.Shopify.routes?.root ?? "/"}search/suggest`, {
    q: searchTerm,
    "resources[type]": resourceTypes.join(","),
    "resources[limit]": limit.toString(),
    section_id: sectionId,
  });

export const navigationSearch = component((node: HTMLElement) => {
  const searchInput = node.querySelector<HTMLInputElement>(
    'input[type="search"]'
  );
  const navSearchResultsContainer = node.querySelector<HTMLDivElement>(
    ".nav-search-results"
  );
  const navSearchResultsPlaceholderContainer =
    node.querySelector<HTMLOListElement>(".nav-search-results-placeholder");
  const navSearchToggle =
    document.querySelector<HTMLLinkElement>(".search-toggle");
  const navSearchClose = node.querySelector<HTMLButtonElement>(".close-button");
  const navSearchClear = node.querySelector<HTMLButtonElement>(".clear-button");
  const navSearchOverlay = node.querySelector<HTMLDivElement>(
    ".nav-search-overlay"
  );
  const navSearchContent = node.querySelector<HTMLDivElement>(
    ".nav-search-content"
  );
  const navSearchTopSuggestions = node.querySelector<HTMLDivElement>(
    ".nav-search-top-suggestions"
  );

  const navSearchTopSuggestionsList =
    navSearchTopSuggestions?.querySelector<HTMLUListElement>("ul");
  const navSearchTopSuggestionItemsLink =
    navSearchTopSuggestions?.querySelectorAll<HTMLLinkElement>("a") ?? [];
  const navSearchTopSuggestionItemsButton =
    navSearchTopSuggestions?.querySelectorAll<HTMLLinkElement>("button") ?? [];

  const bodyElement = document.querySelector<HTMLBodyElement>("body");
  const navigationSearchSection = document.querySelector<HTMLDivElement>(
    `.js-navigation-search`
  );

  const trap = createFocusTrap(node, {
    initialFocus: node,
    setReturnFocus: node,
  });

  const maestroConstants = {
    "dest url": null,
    "dest path": null,
    "from url": window.location.href,
    "from path": window.location.pathname,
    data: null,
    target: "search",
    "page section": node.dataset.mtrSection,
    position: null,
  };

  const searchCache = new Map<string, string>();

  const placeholders = createCardPlaceholders(4);
  navSearchResultsPlaceholderContainer?.append(...placeholders);

  const showPlaceholder = () => {
    navSearchResultsPlaceholderContainer?.classList.remove("hidden");
    navSearchResultsPlaceholderContainer?.classList.add("grid");
    navSearchResultsContainer?.classList.add("hidden");
    navSearchTopSuggestions?.classList.add("hidden");
  };

  const showSearchResults = () => {
    navSearchResultsPlaceholderContainer?.classList.add("hidden");
    navSearchResultsPlaceholderContainer?.classList.remove("grid");
    navSearchResultsContainer?.classList.remove("hidden");
    navSearchTopSuggestions?.classList.add("hidden");
  };

  const hideSearchResults = () => {
    navSearchResultsPlaceholderContainer?.classList.add("hidden");
    navSearchResultsPlaceholderContainer?.classList.remove("grid");
    navSearchResultsContainer?.classList.add("hidden");
    navSearchTopSuggestions?.classList.remove("hidden");
  };

  const fireViewSearchResultsEvent = (searchTerm: string) => {
    const resultsCount =
      navSearchResultsContainer?.querySelector<HTMLElement>(
        `[data-mtr-search-results-count]`
      )?.dataset?.mtrSearchResultsCount ?? "0";

    const navProductNames =
      navSearchResultsContainer?.querySelectorAll<HTMLElement>(
        "[data-mtr-product-name]"
      ) ?? [];

    const searchResults = Array.from(navProductNames)
      .filter((el): el is HTMLElement => el instanceof HTMLElement)
      .map((el) => el.dataset.mtrProductName)
      .filter(Boolean);

    window.maestro.fireTrackEvent("viewSearchResults", {
      ...maestroConstants,
      "search terms": searchTerm,
      "results count": resultsCount,
      "displayed results": searchResults,
    });
  };

  const renderSearchResults = (searchTerm: string, resultsMarkup: string) => {
    if (navSearchResultsContainer) {
      navSearchResultsContainer.innerHTML = resultsMarkup;
    }
    showSearchResults();
    fireViewSearchResultsEvent(searchTerm);
    window.dispatchEvent(
      new Event("shopify:section:load", {
        bubbles: true,
        cancelable: true,
      })
    );
  };

  let fetchController = new AbortController();

  const fetchSearchResults = (searchTerm: string) => {
    if (fetchController.signal.aborted) {
      fetchController = new AbortController();
    }

    if (searchCache.has(searchTerm.toLowerCase())) {
      return searchCache.get(searchTerm.toLowerCase());
    }

    return fetch(
      getPredictiveSearchRequestUrl(
        searchTerm,
        "tbsp-navigation-search-results",
        ["product"],
        4
      ),
      {
        signal: fetchController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(`${response.status}`);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup =
          new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("#shopify-section-tbsp-navigation-search-results")
            ?.innerHTML ?? "";
        searchCache.set(searchTerm.toLowerCase(), resultsMarkup);
        return resultsMarkup;
      })
      .catch((error) => {
        hideSearchResults();
        throw error;
      });
  };

  const handleClear = () => {
    if (searchInput) {
      searchInput.value = "";
    }
    navSearchClear?.classList.add("hidden");
    hideSearchResults();
  };

  type SearchType = "manual" | "suggested";

  const fireSearchSubmitEvent = (
    searchTerm: string,
    searchType: SearchType,
    position: number | null
  ) => {
    window.maestro.fireTrackEvent("submitSiteSearch", {
      ...maestroConstants,
      position,
      "search terms": searchTerm,
      "search type": searchType,
    });
  };

  const handleInput = async (
    e: Event & { target: HTMLElement },
    {
      position,
      searchType,
    }: {
      position: number | null;
      searchType: SearchType;
    } = {
      searchType: "manual",
      position: null,
    }
  ) => {
    if (searchInput?.value) {
      navSearchClear?.classList.remove("hidden");
      const searchTerm = searchInput.value.trim();
      if (searchTerm.length) {
        showPlaceholder();
        fetchController?.abort();
        try {
          const resultsMarkup = await fetchSearchResults(searchTerm);
          fireSearchSubmitEvent(searchTerm, searchType, position);

          if (resultsMarkup) {
            renderSearchResults(searchTerm, resultsMarkup);
          }
        } catch (error) {
          console.error(error);
          hideSearchResults();
        }
      } else {
        hideSearchResults();
      }
    } else {
      navSearchClear?.classList.add("hidden");
      hideSearchResults();
    }
  };

  const handleSearchToggleClick = (e: Event) => {
    e.preventDefault();

    if (bodyElement) {
      bodyElement.dataset.scrollY = window.scrollY.toString();
      setTimeout(() => {
        bodyElement.classList.add(
          "overflow-y-hidden",
          "mg:overflow-y-auto",
          "fixed",
          "mg:static"
        );
      }, 350);
    }
    navigationSearchSection?.classList.remove(
      "invisible",
      "h-0",
      "overflow-hidden"
    );
    navSearchOverlay?.classList.add("mg:block");
    navSearchContent?.classList.remove(
      "animate-fade-out",
      "mg:translate-y-[-367px]"
    );
    navSearchContent?.classList.add("animate-fade-in", "mg:translate-y-0");
    navSearchOverlay?.classList.add("opacity-50");
    searchInput?.focus();
    trap.activate();
  };

  const handleSearchCloseClick = (e: Event) => {
    e.preventDefault();
    setTimeout(() => {
      navigationSearchSection?.classList.add(
        "invisible",
        "h-0",
        "overflow-hidden"
      );
      navSearchOverlay?.classList.remove("mg:block");
      if (navSearchTopSuggestionsList) {
        navSearchTopSuggestionsList.scrollLeft = 0;
      }
      handleClear();
    }, 350);
    navSearchContent?.classList.remove("animate-fade-in", "mg:translate-y-0");
    navSearchContent?.classList.add(
      "animate-fade-out",
      "mg:translate-y-[-367px]"
    );
    navSearchOverlay?.classList.remove("opacity-50");
    bodyElement?.classList.remove(
      "overflow-y-hidden",
      "mg:overflow-y-auto",
      "fixed",
      "mg:static"
    );
    window.scroll({ top: parseInt(bodyElement?.dataset.scrollY ?? "0") });
    bodyElement?.removeAttribute("data-scroll-y");
    trap.deactivate();
    navSearchToggle?.focus();
  };

  const handleEscKey = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;

    if (!navigationSearchSection?.classList.contains("invisible")) {
      handleSearchCloseClick(e);
    }
  };

  const handleTopSuggestionClick =
    (idx: number) => (e: Event & { target: HTMLElement }) => {
      e.preventDefault();
      if (searchInput) {
        searchInput.value = (
          e as MouseEvent & { target: HTMLElement }
        ).target?.innerText;
      }
      navSearchClear?.classList.remove("hidden");
      handleInput(e, {
        searchType: "suggested",
        position: idx,
      });
    };

  const debouncedHandleInput = debounce(handleInput, 300);

  navSearchToggle?.addEventListener("click", handleSearchToggleClick);
  [navSearchClose, navSearchOverlay].forEach((element) =>
    element?.addEventListener("click", handleSearchCloseClick)
  );
  searchInput?.addEventListener("input", debouncedHandleInput);
  window.addEventListener("keydown", handleEscKey);
  navSearchClear?.addEventListener("click", handleClear);
  [
    ...navSearchTopSuggestionItemsLink,
    ...navSearchTopSuggestionItemsButton,
  ].forEach((item, idx) => {
    item.addEventListener("click", handleTopSuggestionClick(idx + 1));
  });

  return () => {
    searchInput?.removeEventListener("input", debouncedHandleInput);
    navSearchToggle?.removeEventListener("click", handleSearchToggleClick);
    [navSearchClose, navSearchOverlay].forEach((element) =>
      element?.removeEventListener("click", handleSearchCloseClick)
    );
    window.removeEventListener("keydown", handleEscKey);
    navSearchClear?.removeEventListener("click", handleClear);
  };
});
